import * as React from "react";
import {none, option, Option} from "ts-option";
import {FormGroupProps} from "../../../common/props/FormGroupProps";
import {PlaceholderProps} from "../../../common/props/PlaceholderProps";
import PhoneNumberMultiSelector from "./PhoneNumberMultiSelector";
import Phone, {PhoneTypeEnum} from "../../../../model/phone/Phone";
import {OnFailure, OnSuccessData} from "../../../../store/action/actionHelpers";
import {PhoneValidationResult} from "../../../../model/validation/PhoneValidationResult";
import {withRouter} from "react-router";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {validatePhone} from "../../../../store/action/validation/validation";
import Toast from "../../../helper/Toast";
import {InputActionMeta} from "react-select/lib/types";
import FormGroup from "../../../../hiberUiKit/src/molecules/FormGroup/FormGroup";

interface IComponentProps {
	/**
	 * Multiselect
	 */
	multi: boolean;
	/**
	 * Number type we expect
	 */
	numberType?: string;
	/**
	 * Maximum number of inputs if multi is set to true.
	 */
	limit?: number
}

interface DispatchProps {
	/**
	 * Validate phone number
	 * @param {string} phoneNumber
	 * @param {OnSuccessData<PhoneValidationResult>} onSuccess
	 * @param {OnFailure} onFailure
	 */
	validatePhone: (phoneNumber: string, onSuccess: OnSuccessData<PhoneValidationResult>, onFailure: OnFailure) => void;
}

interface State {
	/**
	 * Text displayed when create phone and validation error
	 */
	promptText: string
	/**
	 * No result or non unique text
	 */
	noResultText: string
	/**
	 * If component should be in loading state
	 */
	loading: boolean
	/**
	 * Selector input value
	 */
	inputValue: string;
}

type Props = IComponentProps & DispatchProps & FormGroupProps<Phone | Phone[]> & PlaceholderProps;

/**
 * Create phone number form view group.
 */
class PhoneFormGroup extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.onChange = this.onChange.bind(this);
		this.onInputChange = this.onInputChange.bind(this);
		this.onAdd = this.onAdd.bind(this);
		this.state = {promptText: "Create phone number:", noResultText: "", loading: false, inputValue: ""};
	}

	render() {
		return (
			<FormGroup label={this.props.label} error={this.props.field.firstError().getOrElse("")}>
				<PhoneNumberMultiSelector items={none}
										  value={this.props.field.getOptionalValue()}
										  inputValue={this.state.inputValue}
										  onChanged={this.onChange}
										  onInputChange={this.onInputChange}
										  onAddOption={this.onAdd}
										  clearable={false}
										  multi={this.props.multi}
										  placeholder={this.props.placeholder}
										  promptTextCreator={(filterText) => {return `${this.state.promptText} ${filterText}`}}
										  onInputKeyDown={() => {this.setState({promptText: "Create phone number:"})}}
										  noResultsText={this.state.noResultText}
										  loading={this.state.loading}
										  getNewOptionData={(value, label) => {return this.newOptionCreator(label)}}/>
			</FormGroup>
		);
	}

	/**
	 * Callback on changed phone list.
	 * @param data
	 */
	onChange(data: Option<Phone[]>) {
		this.props.onChange(this.props.field.updateValue(data.isDefined && data.get.length > 0 ? data : none))
	}

	/**
	 * Selector input change callback.
	 * @param {string} inputValue
	 * @param {InputActionMeta} inputActionMeta
	 */
	onInputChange(inputValue: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({inputValue});
		}
	}

	/**
	 * Callback on add new phone
	 * @param newPhone
	 */
	onAdd(newPhone: string) {
		const newItem = this.newOptionCreator(newPhone);
		if (!newItem.internationalPhoneNumber.length)
			return;
		const phoneNumbers = this.props.field.getOptionalValue().map(v => {
			if (v instanceof Phone) {
				return [v]
			}
			return v;
		}).getOrElse([]);
		if (this.props.multi && this.props.limit && this.props.limit <= phoneNumbers.length) {
			return;
		}
		this.setState({loading: true}, () =>
			this.props.validatePhone(newItem.nationalPhoneNumber, (result) => {
					if (result.isValid) {
						const phones = this.props.field.value.getOrElse([]) as Phone[];
						if (this.props.multi) {
							if (this.isUnique(newItem.internationalPhoneNumber, phones)) {
								this.setState({inputValue: ""},
									() => this.onChange(option(phones.concat(
										newItem.copy({
												type: option(result.numberType),
												internationalPhoneNumber: result.internationalPhoneNumber,
												nationalPhoneNumber: result.nationalPhoneNumber,
											}
										)))));
							}
							else {
								this.setState({promptText: "Phone number already exists: "})
							}
						}
						else {
							if (this.props.numberType) {
								if (this.props.numberType == result.numberType) {
									this.props.onChange(
										this.props.field.updateValue(option(newItem.copy({
											type: option(result.numberType),
											internationalPhoneNumber: result.internationalPhoneNumber,
											nationalPhoneNumber: result.nationalPhoneNumber
										}))))
								}
								else {
									this.setState({promptText: "Invalid phone number type"})
								}
							}
							else {
								this.props.onChange(
									this.props.field.updateValue(option(newItem.copy({type: option(result.numberType)}))))
							}
						}
					}
					else {
						this.setState({promptText: "Phone number not valid: "})
					}
					this.setState({loading: false});
				},
				() => {

					const phones = this.props.field.value.getOrElse([]) as Phone[];
					if (this.props.multi) {
						if (this.isUnique(newItem.internationalPhoneNumber, phones)) {
							this.setState({inputValue: ""},
								() => this.onChange(option(phones.concat(
									newItem.copy({
											type: option(PhoneTypeEnum.Mobile),
											internationalPhoneNumber: newItem.internationalPhoneNumber,
											nationalPhoneNumber: newItem.internationalPhoneNumber,
										}
									)))));
						}
						else {
							this.setState({promptText: "Phone number already exists: "})
						}
					}
					else {
						if (this.props.numberType) {

								this.props.onChange(
									this.props.field.updateValue(option(newItem.copy({
										type: option(PhoneTypeEnum.Mobile),
										internationalPhoneNumber: newItem.internationalPhoneNumber,
										nationalPhoneNumber: newItem.internationalPhoneNumber,
									}))))

						}
						else {
							this.props.onChange(
								this.props.field.updateValue(option(newItem.copy({type: option(PhoneTypeEnum.Mobile)}))))
						}
					}


				Toast.error("Sorry, something went wrong. Try again later.", "Validate phone number");})
		)
	}

	isUnique(newItem: string, options: Phone[]) {
		return !options.filter(i => i.internationalPhoneNumber == newItem).length;
	}

	newOptionCreator(newOption: string): Phone {
		return new Phone(newOption.trim(), newOption, none, none);
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		validatePhone: (phoneNumber: string, onSuccess: OnSuccessData<PhoneValidationResult>, onFailure: OnFailure) => dispatch(
			validatePhone(phoneNumber, onSuccess, onFailure))
	}
}

export default withRouter(connect(null, mapDispatchToProps)(PhoneFormGroup));
